import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Phone, Mail, MapPin, Calendar, Trash2, Sparkles, Send, Loader2, CheckCircle2, AlertCircle, Lock } from "lucide-react";
import { serviceLabel } from "@/lib/pricing";
import { LeadDisputeButton } from "@/pages/MarketplaceTab";
import { SourceChip } from "@/pages/Sources";

const STATUSES = ["NEW", "CONTACTED", "SCHEDULED", "WON", "LOST"];

export default function LeadDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [lead, setLead] = useState(null);
  const [company, setCompany] = useState(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([api.get(`/leads/${id}`), api.get("/companies/me")])
      .then(([l, c]) => { setLead(l.data); setNotes(l.data.notes || ""); setCompany(c.data); })
      .catch(() => nav("/leads"));
  }, [id, nav]);

  const setStatus = async (status) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/leads/${id}`, { status });
      setLead(data);
      toast.success(`Marked ${status}`);
    } catch {
      toast.error("Couldn't update");
    } finally { setSaving(false); }
  };

  const saveNotes = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/leads/${id}`, { notes });
      setLead(data);
      toast.success("Notes saved");
    } catch {
      toast.error("Couldn't save notes");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this quote?")) return;
    try {
      await api.delete(`/leads/${id}`);
      toast.success("Quote deleted");
      nav("/leads");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't delete";
      toast.error(msg);
    }
  };

  const [resending, setResending] = useState(false);
  const [lastResend, setLastResend] = useState(null);

  const resendHomeownerEmail = async () => {
    if (!lead?.email) {
      toast.error("This lead has no email on file.");
      return;
    }
    setResending(true);
    try {
      const { data } = await api.post(`/leads/${id}/email/resend-homeowner`);
      const attempt = data?.attempt;
      setLastResend(attempt || null);
      if (attempt?.status === "sent") {
        toast.success(`Quote re-sent to ${attempt.to}`);
      } else if (attempt?.status === "failed") {
        toast.error(`Resend failed: ${attempt.error || "unknown error"}`);
      } else if (attempt?.status === "skipped_no_api_key") {
        toast.error("Resend API key not configured on this environment.");
      } else if (attempt?.status === "skipped_no_recipient") {
        toast.error("No valid email on file for this lead.");
      } else {
        toast.success("Resend triggered");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't resend");
    } finally {
      setResending(false);
    }
  };

  const isMarketplace = !!lead?.marketplace?.assignment_id;

  if (!lead) return <AppShell title="Quote"><div className="text-slate-500 text-sm">Loading…</div></AppShell>;

  const b = lead.estimate_breakdown || {};

  return (
    <AppShell company={company}>
      <Link to="/leads" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4" data-testid="back-to-leads">
        <ArrowLeft className="w-4 h-4" /> All quotes
      </Link>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Free-tier lock overlay */}
          {lead.is_locked && (
            <div data-testid="lead-detail-lock-banner" className="bg-gradient-to-br from-[#0F172A] to-[#1E293B] rounded-2xl p-6 sm:p-7 text-white">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-3 py-1 text-[10px] font-extrabold uppercase tracking-widest mb-3">
                <Lock className="w-3.5 h-3.5 text-[#E2A878]" /> Contact info locked
              </div>
              <h1 className="font-display text-2xl sm:text-3xl font-bold leading-tight" data-testid="lead-name">
                {lead.first_name} quoted <span className="text-[#E2A878]">${Number(lead.estimate_total).toLocaleString()}</span> in {lead.zip_code || "your area"}
              </h1>
              <p className="text-sm text-white/70 mt-3 leading-relaxed max-w-lg">
                This is a real homeowner who quoted through <strong>your</strong> funnel. Unlock their phone, email, and address with a $1 trial. If you don't close them, cancel anytime.
              </p>
              <div className="mt-5 flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={() => nav("/billing")}
                  data-testid="lead-detail-unlock-btn"
                  className="rounded-xl bg-[#C75D33] hover:bg-[#A84C28] text-white h-12 px-6 font-bold"
                >
                  Unlock — $1 trial, cancel anytime →
                </Button>
                <Link to="/leads" className="rounded-xl border border-white/20 text-white h-12 px-6 inline-flex items-center justify-center hover:bg-white/5">
                  Back to quotes
                </Link>
              </div>
            </div>
          )}

          {!lead.is_locked && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h1 className="font-display text-3xl font-bold text-slate-900" data-testid="lead-name">{lead.first_name} {lead.last_name}</h1>
                <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-600">
                  <span className="flex items-center gap-1.5"><Phone className="w-4 h-4" /> {lead.phone}</span>
                  <span className="flex items-center gap-1.5"><Mail className="w-4 h-4" /> {lead.email}</span>
                  <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {lead.zip_code}</span>
                  <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {new Date(lead.created_at).toLocaleString()}</span>
                </div>
                {lead.attribution?.last_touch?.network_group && (
                  <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="lead-source-row">
                    <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Source</span>
                    <SourceChip group={lead.attribution.last_touch.network_group} />
                    {lead.attribution.last_touch.utm_campaign && (
                      <span className="text-xs text-slate-500 truncate max-w-xs" title={lead.attribution.last_touch.utm_campaign}>
                        · {lead.attribution.last_touch.utm_campaign}
                      </span>
                    )}
                    {lead.attribution.first_touch?.network_group && lead.attribution.first_touch.network_group !== lead.attribution.last_touch.network_group && (
                      <span className="text-[11px] text-slate-500">
                        · first touch: <SourceChip group={lead.attribution.first_touch.network_group} />
                      </span>
                    )}
                  </div>
                )}
              </div>
              <Button variant="outline" onClick={remove} disabled={isMarketplace} className="rounded-xl text-red-600 border-red-200 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="delete-lead-btn" title={isMarketplace ? "Marketplace leads can't be deleted — use 'Request credit' instead." : "Delete this quote"}>
                <Trash2 className="w-4 h-4 mr-1" /> Delete
              </Button>
            </div>
            {lead.marketplace?.assignment_id && (
              <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between gap-3 flex-wrap" data-testid="lead-marketplace-card">
                <div className="flex items-center gap-2 text-xs">
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#C75D33]/10 text-[#C75D33] font-extrabold uppercase tracking-widest text-[10px]">
                    <Sparkles className="w-3 h-3" /> Marketplace lead
                  </span>
                  <span className="text-slate-600">
                    Charged <strong className="text-slate-900">${(lead.marketplace.charge_cents / 100).toFixed(2)}</strong> · {String(lead.marketplace.priority_tier || "").toUpperCase()} tier
                  </span>
                </div>
                <LeadDisputeButton lead={lead} onResolved={() => api.get(`/leads/${id}`).then(({ data }) => setLead(data))} />
              </div>
            )}
          </div>
          )}

          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h2 className="font-display text-lg font-bold text-slate-900 mb-4">Quote breakdown</h2>
            <div className="divide-y divide-slate-100">
              {(b.line_items || []).map((li, i) => (
                <div key={i} className="flex justify-between py-2.5 text-sm">
                  <span className="text-slate-700">{li.label} <span className="text-slate-400">× {li.qty}</span></span>
                  <span className="font-semibold text-slate-900">${Number(li.amount).toFixed(2)}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-slate-200 space-y-1.5">
              {b.discount > 0 && (
                <div className="flex justify-between text-sm"><span className="text-slate-600">Discount{b.discount_percent ? ` (${b.discount_percent}%)` : ""}</span><span className="text-emerald-600">−${Number(b.discount).toFixed(2)}</span></div>
              )}
              {b.tax > 0 && (
                <div className="flex justify-between text-sm"><span className="text-slate-600">Tax</span><span>${Number(b.tax).toFixed(2)}</span></div>
              )}
              <div className="flex justify-between text-lg pt-2"><span className="font-bold text-slate-900">Total quote</span><span className="font-bold text-[#C75D33]" data-testid="lead-total">${Number(lead.estimate_total).toFixed(2)}</span></div>
            </div>
          </div>

          {lead.services?.[0]?.items?.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h2 className="font-display text-lg font-bold text-slate-900 mb-3">Measurements</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {lead.services.flatMap((svc, si) =>
                  (svc.items || []).map((it, ii) => (
                    <div key={`${si}-${ii}`} className="rounded-xl bg-slate-50 p-3">
                      <div className="text-xs text-slate-500 uppercase tracking-wider font-bold">{serviceLabel(svc.service_key)} #{ii + 1}</div>
                      <div className="text-sm font-semibold text-slate-900 mt-1">{it.width || "?"}" W × {it.height || "?"}" H</div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h2 className="font-display text-lg font-bold text-slate-900 mb-3">Notes</h2>
            <Textarea
              data-testid="lead-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Internal notes about this quote…"
              className="min-h-[120px] rounded-xl"
            />
            <Button onClick={saveNotes} disabled={saving} className="mt-3 bg-slate-900 hover:bg-slate-800 rounded-xl" data-testid="save-notes-btn">
              Save notes
            </Button>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Status</div>
            <Select value={lead.status} onValueChange={setStatus}>
              <SelectTrigger className="h-11 rounded-xl" data-testid="status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>

            <div className="mt-4 grid grid-cols-2 gap-2">
              {STATUSES.map((s) => (
                <Button
                  key={s}
                  variant={lead.status === s ? "default" : "outline"}
                  size="sm"
                  onClick={() => setStatus(s)}
                  data-testid={`status-btn-${s}`}
                  className={`rounded-xl text-xs ${lead.status === s ? "bg-[#0F172A] hover:bg-black" : ""}`}
                >
                  {s}
                </Button>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Services</div>
            <div className="space-y-2">
              {lead.services.map((s, i) => (
                <div key={i} className="text-sm">
                  <div className="font-semibold text-slate-900">{serviceLabel(s.service_key)}</div>
                  <div className="text-slate-500">{s.quantity} screens{s.need_help ? " · needs measuring help" : ""}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Resend quote email — for "I never got my quote" support cases */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="lead-resend-card">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Customer email</div>
            <p className="text-xs text-slate-600 leading-relaxed mb-3">
              Didn't get their quote? Re-fire the confirmation to <strong className="text-slate-900 break-all">{lead.email || "—"}</strong> in one click.
            </p>
            <Button
              onClick={resendHomeownerEmail}
              disabled={resending || !lead.email}
              className="w-full bg-[#0F172A] hover:bg-black rounded-xl h-10"
              data-testid="resend-homeowner-btn"
            >
              {resending ? (
                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Resending…</>
              ) : (
                <><Send className="w-4 h-4 mr-1.5" /> Resend quote email</>
              )}
            </Button>
            {lastResend && (
              <div
                data-testid="resend-result"
                className={`mt-3 rounded-xl px-3 py-2 text-xs border ${
                  lastResend.status === "sent"
                    ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                    : "bg-amber-50 border-amber-200 text-amber-900"
                }`}
              >
                <div className="flex items-start gap-1.5 font-semibold">
                  {lastResend.status === "sent" ? (
                    <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  ) : (
                    <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  )}
                  <span>
                    {lastResend.status === "sent" && `Sent · resend_id ${lastResend.resend_id || "—"}`}
                    {lastResend.status === "failed" && "Failed at Resend"}
                    {lastResend.status === "skipped_no_recipient" && "Skipped — no recipient"}
                    {lastResend.status === "skipped_no_api_key" && "Skipped — API key missing"}
                  </span>
                </div>
                {lastResend.error && (
                  <div className="mt-1 font-mono text-[10px] text-amber-900/80 break-all">{lastResend.error}</div>
                )}
              </div>
            )}
          </div>

          {lead.sms_consent && (
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-800">
              ✓ Customer opted in to SMS updates
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
